import loadMujoco, { type MjData, type MjModel } from "@mujoco/mujoco";

import { fetchBuffer, fetchText } from "../assets";
import type { BundleManifest, PolicyManifest } from "../types";

export type MujocoModule = Awaited<ReturnType<typeof loadMujoco>>;

export interface EpisodePerturbation {
  seed: number;
  robotYaw: number;
  cubeX: number;
  cubeY: number;
  cubeYaw: number;
}

export class ReposeSim {
  readonly jointQpos: number[];
  readonly jointQvel: number[];
  readonly actuatorCtrl: number[];
  readonly rootBodyId: number;
  readonly cubeBodyId: number;

  private constructor(
    readonly mj: MujocoModule,
    readonly model: MjModel,
    readonly data: MjData,
    readonly policy: PolicyManifest,
  ) {
    this.jointQpos = [];
    this.jointQvel = [];
    this.actuatorCtrl = [];
    for (const name of policy.action.joint_names) {
      const joint = model.jnt(name);
      const actuator = model.actuator(name);
      this.jointQpos.push(Number(model.jnt_qposadr[joint.id]));
      this.jointQvel.push(Number(model.jnt_dofadr[joint.id]));
      this.actuatorCtrl.push(actuator.id);
      joint.delete();
      actuator.delete();
    }
    const root = model.body("pelvis");
    const cube = model.body("object");
    this.rootBodyId = root.id;
    this.cubeBodyId = cube.id;
    root.delete();
    cube.delete();
  }

  static async create(
    bundle: BundleManifest,
    policy: PolicyManifest,
    progress: (fraction: number, detail: string) => void,
  ): Promise<ReposeSim> {
    progress(0.16, "Starting MuJoCo WebAssembly…");
    const mj = await loadMujoco();
    const vfs = new mj.MjVFS();
    try {
      const files = bundle.files.filter((item) =>
        item.path === "scene/g1.xml" || item.path.startsWith("scene/meshes/"),
      );
      let loaded = 0;
      await Promise.all(files.map(async (item) => {
        const buffer = await fetchBuffer(item.path);
        const name = item.path.replace(/^scene\//, "");
        vfs.addBuffer(name, new Uint8Array(buffer));
        loaded += 1;
        progress(0.17 + 0.19 * (loaded / files.length), `Loading robot geometry ${loaded}/${files.length}`);
      }));
      const xml = await fetchText("scene/scene.xml");
      const model = mj.MjModel.from_xml_string(xml, vfs);
      const data = new mj.MjData(model);
      const sim = new ReposeSim(mj, model, data, policy);
      sim.reset();
      progress(0.38, "MuJoCo scene ready");
      return sim;
    } finally {
      vfs.delete();
    }
  }

  reset(): void {
    this.mj.mj_resetDataKeyframe(this.model, this.data, 0);
    this.mj.mj_forward(this.model, this.data);
  }

  /** Deterministic, paired perturbations for the headless A/B evaluator. */
  resetPerturbed(seed: number): EpisodePerturbation {
    this.reset();
    let state = seed >>> 0;
    const random = (): number => {
      state ^= state << 13;
      state ^= state >>> 17;
      state ^= state << 5;
      return (state >>> 0) / 0x1_0000_0000;
    };
    const robotYaw = (random() * 12 - 6) * Math.PI / 180;
    const cubeX = 0.66 + random() * 0.26;
    const cubeY = (random() * 2 - 1) * 0.10;
    const cubeYaw = (random() * 2 - 1) * Math.PI;

    const rootJoint = this.model.jnt("floating_base_joint");
    const rootAt = Number(this.model.jnt_qposadr[rootJoint.id]);
    rootJoint.delete();
    this.data.qpos[rootAt + 3] = Math.cos(robotYaw / 2);
    this.data.qpos[rootAt + 4] = 0;
    this.data.qpos[rootAt + 5] = 0;
    this.data.qpos[rootAt + 6] = Math.sin(robotYaw / 2);

    const cubeJoint = this.model.jnt("cube_joint");
    const cubeAt = Number(this.model.jnt_qposadr[cubeJoint.id]);
    cubeJoint.delete();
    this.data.qpos[cubeAt] = cubeX;
    this.data.qpos[cubeAt + 1] = cubeY;
    const halfYaw = cubeYaw / 2;
    const yawW = Math.cos(halfYaw);
    const yawZ = Math.sin(halfYaw);
    const baseW = Math.SQRT1_2;
    const baseY = -Math.SQRT1_2;
    this.data.qpos[cubeAt + 3] = yawW * baseW;
    this.data.qpos[cubeAt + 4] = -yawZ * baseY;
    this.data.qpos[cubeAt + 5] = yawW * baseY;
    this.data.qpos[cubeAt + 6] = yawZ * baseW;
    this.mj.mj_forward(this.model, this.data);
    return { seed, robotYaw, cubeX, cubeY, cubeYaw };
  }

  jointPosition(out = new Float32Array(29)): Float32Array {
    for (let i = 0; i < this.jointQpos.length; i += 1) out[i] = this.data.qpos[this.jointQpos[i] ?? 0];
    return out;
  }

  jointVelocity(out = new Float32Array(29)): Float32Array {
    for (let i = 0; i < this.jointQvel.length; i += 1) out[i] = this.data.qvel[this.jointQvel[i] ?? 0];
    return out;
  }

  rootPose(): { pos: Float64Array; quat: Float64Array } {
    const body = this.data.body(this.rootBodyId);
    const pose = { pos: body.xpos as Float64Array, quat: body.xquat as Float64Array };
    body.delete();
    return pose;
  }

  cubePose(): { pos: Float64Array; quat: Float64Array } {
    const body = this.data.body(this.cubeBodyId);
    const pose = { pos: body.xpos as Float64Array, quat: body.xquat as Float64Array };
    body.delete();
    return pose;
  }

  applyAction(action: Float32Array): void {
    for (let i = 0; i < this.actuatorCtrl.length; i += 1) {
      const target = (this.policy.action.default_joint_pos[i] ?? 0)
        + (this.policy.action.scale[i] ?? 0) * (action[i] ?? 0);
      this.data.ctrl[this.actuatorCtrl[i] ?? 0] = target;
    }
  }

  stepPhysics(): void {
    this.mj.mj_step(this.model, this.data);
  }

  dispose(): void {
    this.data.delete();
    this.model.delete();
  }
}
